import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Paper,
  Box,
  Typography,
  MenuItem,
  Switch,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Chip,
  Tooltip,
  Autocomplete,
  IconButton,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Layout from "./Layout";
import { useAuth } from "../context/AuthProvider";
// A subcomponent for handling subdirectory tags input
const SubDirectoryTagInput = ({
  includeTags,
  setIncludeTags,
  excludeTags,
  setExcludeTags,
}) => {
  const [includeValue, setIncludeValue] = useState("");
  const [excludeValue, setExcludeValue] = useState("");

  const handleAddTag = (value, tags, setTags, clearInput) => {
    const trimmed = value.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      clearInput("");
    }
  };

  const handleDeleteTag = (tag, tags, setTags) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const showInclude = excludeTags.length === 0 && excludeValue.trim() === "";
  const showExclude = includeTags.length === 0 && includeValue.trim() === "";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 2 }}>
      {/* Include Section */}
      {showInclude && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 1 }}>
              Include Files
            </Typography>
            <Tooltip
              title="Only the selected files and folders will be used for scanning."
              arrow
              PopperProps={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [10, 10],
                    },
                  },
                ],
              }}
            >
              <IconButton size="small" sx={{ p: 0.5 }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {includeTags.map((tag, idx) => (
              <Chip
                key={idx}
                label={tag}
                onDelete={() =>
                  handleDeleteTag(tag, includeTags, setIncludeTags)
                }
                color="primary"
              />
            ))}
            <TextField
              value={includeValue}
              onChange={(e) => setIncludeValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  handleAddTag(
                    includeValue,
                    includeTags,
                    setIncludeTags,
                    setIncludeValue
                  );
                }
              }}
              placeholder="Add include path and press Enter or ','"
              size="small"
              sx={{ minWidth: 300 }}
            />
          </Box>
        </Box>
      )}

      {/* Exclude Section */}
      {showExclude && (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mr: 1 }}>
              Exclude Files
            </Typography>
            <Tooltip
              title="Only the selected files and folders will not be used for scanning."
              arrow
              PopperProps={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [10, 10],
                    },
                  },
                ],
              }}
            >
              <IconButton size="small" sx={{ p: 0.5 }}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {excludeTags.map((tag, idx) => (
              <Chip
                key={idx}
                label={tag}
                onDelete={() =>
                  handleDeleteTag(tag, excludeTags, setExcludeTags)
                }
                color="error"
              />
            ))}
            <TextField
              value={excludeValue}
              onChange={(e) => setExcludeValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  handleAddTag(
                    excludeValue,
                    excludeTags,
                    setExcludeTags,
                    setExcludeValue
                  );
                }
              }}
              placeholder="Add exclude path and press Enter or ','"
              size="small"
              sx={{ minWidth: 300 }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
const SrcDirectoryTagInput = ({ srcDirectoryTags, setSrcDirectoryTags }) => {
  const [srcValue, setSrcValue] = useState("");

  const handleInputChangesrc = (e) => setSrcValue(e.target.value);

  const handleKeyDownsrc = (e) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      const trimmedValue = srcValue.trim();
      if (trimmedValue && !srcDirectoryTags.includes(trimmedValue)) {
        setSrcDirectoryTags([...srcDirectoryTags, trimmedValue]);
        setSrcValue("");
      }
    }
  };

  const handleDeletesrc = (tagToDelete) => {
    setSrcDirectoryTags(srcDirectoryTags.filter((tag) => tag !== tagToDelete));
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
      {srcDirectoryTags.map((tag, index) => (
        <Chip
          key={index}
          label={tag}
          onDelete={() => handleDeletesrc(tag)}
          color="primary"
        />
      ))}
      <TextField
        value={srcValue}
        onChange={handleInputChangesrc}
        onKeyDown={handleKeyDownsrc}
        placeholder="Add Source directory and press Enter or ','"
        variant="outlined"
        size="small"
        sx={{ minWidth: 300, fontSize: "2px" }}
      />
    </Box>
  );
};
const IncludeDirectoryTagInput = ({
  includeDirectoryTags,
  setIncludeDirectoryTags,
}) => {
  const [includedirValue, setIncludedirValue] = useState("");

  const handleInputChangeinclude = (e) => setIncludedirValue(e.target.value);

  const handleKeyDowninclude = (e) => {
    if (e.key === "," || e.key === "Enter") {
      e.preventDefault();
      const trimmedValue = includedirValue.trim();
      if (trimmedValue && !includeDirectoryTags.includes(trimmedValue)) {
        setIncludeDirectoryTags([...includeDirectoryTags, trimmedValue]);
        setIncludedirValue("");
      }
    }
  };

  const handleDeleteinclude = (tagToDelete) => {
    setIncludeDirectoryTags(
      includeDirectoryTags.filter((tag) => tag !== tagToDelete)
    );
  };

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
      {includeDirectoryTags.map((tag, index) => (
        <Chip
          key={index}
          label={tag}
          onDelete={() => handleDeleteinclude(tag)}
          color="primary"
        />
      ))}
      <TextField
        value={includedirValue}
        onChange={handleInputChangeinclude}
        onKeyDown={handleKeyDowninclude}
        placeholder="Add Include directory and press Enter or ','"
        variant="outlined"
        size="small"
        sx={{ minWidth: 300, fontSize: "2px" }}
      />
    </Box>
  );
};
const CreateProject = () => {
  const navigate = useNavigate();
  const [useSubDirectory, setUseSubDirectory] = useState(false);
  const [subDirectoryTags, setSubDirectoryTags] = useState([]);
  const [includeTags, setIncludeTags] = useState([]);
  const [excludeTags, setExcludeTags] = useState([]);
  const [useSrcDirectory, setUseSrcDirectory] = useState(false);
  const [srcDirectoryTags, setSrcDirectoryTags] = useState([]);
  const [useIncludeDirectory, setUseIncludeDirectory] = useState(false);
  const [includeDirectoryTags, setIncludeDirectoryTags] = useState([]);
  const { authToken, planId } = useAuth();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const token = localStorage.getItem("access_token");
  const fileInputRef = useRef(null);
  const [error, setError] = useState(null);

  const [project, setProject] = useState({
    name: "",
    description: "",
    repoProvider: "github", // default value
    codeLanguage: "",
    msu_definition: "",
    clientRepoUrl: "",
    mainFileName: "",
    created_date: new Date().toISOString().split("T")[0],
    compilerVersion: "",
  });

  const [configurations, setConfigurations] = useState({
    // version: '',
    latestConfig: {
      buildTool: {
        tool_id: "",
        toolName: "",
      },
      staticAnalysisTools: [],
      unitTestingTools: [],
      memoryTestingTools: [],
      cybersecurityAnalysisTools: [],
      fossComplianceTools: []
    },
  });

  const [tools, setTools] = useState({
    buildTool: [],
    staticAnalysisTools: [],
    unitTestingTools: [],
    memoryTestingTools: [],
    cybersecurityAnalysisTools: [],
    fossComplianceTools: []
  });

  // Java versions for Kotlin compiler selection
  const javaVersions = [
    "Java 8",
    "Java 11",
    "Java 17",
    "Java 21",
  ];

  const [organizationType, setOrganizationType] = useState(null);
  const [genericTools, setGenericTools] = useState([]); // tools with genericAvailability === true
  const [selectedGenericTools, setSelectedGenericTools] = useState([]);

  // For multi-select menus in stage configuration (if used for "O")
  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const msuOptions = [
    "Definition A",
    "Definition B",
    "Definition C",
    "Definition D",
    "Definition E",
    "Definition F",
    "Definition G",
    "Definition H",
  ];

  const handleMsuChange = (event, newValue) => {
    handleChange({
      target: {
        name: "msu_definition",
        value: newValue,
      },
    });
  };
  // Fetch available tools from the backend
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/profile`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        // Assuming the profile data contains an object "organization" with a key "organization_type"
        const orgType = data.organization?.organization_type;
        setOrganizationType(orgType);
      })
      .catch((err) => {
        console.error("Error fetching organization profile:", err);
        setError("Error fetching organization profile.");
      });
  }, [token]);

  // Fetch available tools for both stage configuration and generic tools when org type is "I"
  useEffect(() => {
    // Fetch all tools (this endpoint is used for stage configuration too)
    fetch(
      `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/tools-by-plan?plan_id=${planId}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch tools");
        }
        return response.json();
      })
      .then((data) => {
        // Separate tools by their type for stage configuration (for org type "O")
        const buildTool = data.filter((tool) => tool.toolType === "buildTool");
        const staticAnalysisTools = data.filter(
          (tool) => tool.toolType === "staticAnalysisTool"
        );
        const unitTestingTools = data.filter(
          (tool) => tool.toolType === "unitTestingTool"
        );
        const memoryTestingTools = data.filter(
          (tool) => tool.toolType === "memoryTestingTool"
        );
        const cybersecurityAnalysisTools = data.filter(
          (tool) => tool.toolType === "cyberSecurityAnalysisTool"
        );
        const fossComplianceTools = data.filter(
          (tool) => tool.toolType === "fossComplianceTool"
        );

        setTools({
          buildTool,
          staticAnalysisTools,
          unitTestingTools,
          memoryTestingTools,
          cybersecurityAnalysisTools,
          fossComplianceTools
        });
        // // For organization type "I", filter tools that have genericAvailability set to true
        // if (organizationType === "I") {
        //   const availableGeneric = data.filter(
        //     (tool) =>
        //       tool.genericAvailability === true ||
        //       tool.genericAvailability === "true"
        //   );
        //   setGenericTools(availableGeneric);
        // }
      })
      .catch((err) => {
        console.error("Error fetching tools:", err);
        setError("Error loading tools. Please try again later.");
      });
  }, [token, planId]);

  const [hasInitialized, setHasInitialized] = useState({
    buildTool: false,
    staticAnalysisTools: false,
    unitTestingTools: false,
    memoryTestingTools: false,
    cybersecurityAnalysisTools: false,
    fossComplianceTools: false,
  });

  useEffect(() => {
    const updatedConfig = {};

    if (
      tools.staticAnalysisTools.length > 0 &&
      !hasInitialized.staticAnalysisTools
    ) {
      updatedConfig.staticAnalysisTools = tools.staticAnalysisTools.map((tool) => ({
          tool_id: tool.tool_id,
          toolName: tool.toolName,
      }));
    }

    if (
      tools.memoryTestingTools.length > 0 &&
      !hasInitialized.memoryTestingTools
    ) {
      updatedConfig.memoryTestingTools = tools.memoryTestingTools.map((tool) => ({
        tool_id: tool.tool_id,
        toolName: tool.toolName,
      }));
    }

    if (
      tools.unitTestingTools.length > 0 &&
      !hasInitialized.unitTestingTools
    ) {
      updatedConfig.unitTestingTools = tools.unitTestingTools.map((tool) => ({
        tool_id: tool.tool_id,
        toolName: tool.toolName,
      }));
    }

    if (
      tools.cybersecurityAnalysisTools.length > 0 &&
      !hasInitialized.cybersecurityAnalysisTools
    ) {
      updatedConfig.cybersecurityAnalysisTools = tools.cybersecurityAnalysisTools.map((tool) => ({
          tool_id: tool.tool_id,
          toolName: tool.toolName,
        }));
    }

    if (
      tools.fossComplianceTools.length > 0 &&
      !hasInitialized.fossComplianceTools
    ) {
      updatedConfig.fossComplianceTools = tools.fossComplianceTools.map((tool) => ({
          tool_id: tool.tool_id,
          toolName: tool.toolName,
      }));
    }

    if (
      tools.buildTool.length > 0 &&
      !hasInitialized.buildTool
    ) {
      updatedConfig.buildTool = {
        tool_id: tools.buildTool[0].tool_id,
        toolName: tools.buildTool[0].toolName,
      };
    }

    // Apply config and update flags
    if (Object.keys(updatedConfig).length > 0) {
      setConfigurations((prev) => ({
        ...prev,
        latestConfig: {
          ...prev.latestConfig,
          ...updatedConfig,
        },
      }));

      setHasInitialized((prev) => ({
        ...prev,
        ...(updatedConfig.buildTool && { buildTool: true }),
        ...(updatedConfig.staticAnalysisTools && { staticAnalysisTools: true }),
        ...(updatedConfig.unitTestingTools && { unitTestingTools: true }),
        ...(updatedConfig.memoryTestingTools && { memoryTestingTools: true }),
        ...(updatedConfig.cybersecurityAnalysisTools && { cybersecurityAnalysisTools: true }),
        ...(updatedConfig.fossComplianceTools && { fossComplianceTools: true }),
      }));
    }
  }, [tools]);


  // Preselect the first static analysis tool if organization type is "I"
  // useEffect(() => {
  //   if (
  //     tools.staticAnalysisTools.length > 0 &&
  //     configurations.latestConfig.staticAnalysisTools.length === 0
  //   ) {
  //     const allTools = tools.staticAnalysisTools.map((tool) => ({
  //       tool_id: tool.tool_id,
  //       toolName: tool.toolName,
  //       toolVersion: tool.toolVersion,
  //       immutable: tool.immutable || false,
  //     }));

  //     setConfigurations((prev) => ({
  //       ...prev,
  //       latestConfig: {
  //         ...prev.latestConfig,
  //         staticAnalysisTools: allTools,
  //       },
  //     }));
  //   }
  // }, [
  //   tools.staticAnalysisTools,
  //   configurations.latestConfig.staticAnalysisTools.length,
  // ]);

  // Set file input attributes for folder selection
  useEffect(() => {
    if (fileInputRef.current) {
      fileInputRef.current.setAttribute("webkitdirectory", "true");
      fileInputRef.current.setAttribute("directory", "true");
    }
  }, []);

  // Handlers for input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProject((prev) => ({ ...prev, [name]: value }));
  };

  const handleRepoProviderChange = (e) => {
    const value = e.target.value;
    // Reset clientRepoUrl when provider changes.
    setProject((prev) => ({ ...prev, repoProvider: value, clientRepoUrl: "" }));
  };

  // Capture selected files/folders (using webkitdirectory for folder selection)
  const handleFileSelection = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    console.log("Selected files:", files);
  };

  const handleConfigChange = (e, type) => {
    const { value } = e.target;
    // console.log(type);
    if (
      type === "staticAnalysisTools" ||
      type === "unitTestingTools" ||
      type === "memoryTestingTools" ||
      type === "cybersecurityAnalysisTools" ||
      type === "fossComplianceTools"
    ) {
      const selectedTools = tools[type].filter((tool) =>
        value.includes(tool.tool_id)
      );
      setConfigurations((prev) => ({
        ...prev,
        latestConfig: {
          ...prev.latestConfig,
          [type]: selectedTools.map((tool) => ({
            tool_id: tool.tool_id,
            toolName: tool.toolName,
          })),
        },
      }));
    } else if (type === "buildTool") {
      if (value === "") {
        // Handle "None" selection
        setConfigurations((prev) => ({
          ...prev,
          latestConfig: {
            ...prev.latestConfig,
            [type]: {
              tool_id: "",
              toolName: "",
            },
          },
        }));
      } else {
        const selectedTool = tools.buildTool.find(
          (tool) => tool.tool_id === value
        );
        if (selectedTool) {
          setConfigurations((prev) => ({
            ...prev,
            latestConfig: {
              ...prev.latestConfig,
              [type]: {
                tool_id: selectedTool.tool_id,
                toolName: selectedTool.toolName,
              },
            },
          }));
        }
      }
    }
  };

  // // Handler for selecting generic tools (for org type "I")
  // const handleGenericToolSelection = (toolId) => {
  //   setSelectedGenericTools((prevSelected) =>
  //     prevSelected.includes(toolId)
  //       ? prevSelected.filter((id) => id !== toolId)
  //       : [...prevSelected, toolId]
  //   );
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (project.repoProvider === "manual" && selectedFiles.length === 0) {
      setError("Please select files to upload for manual repository");
      return;
    }

    // If repoProvider is manual, we send FormData with files; otherwise, send JSON.
    if (project.repoProvider === "manual") {
      // Build FormData payload for file upload.
      const formData = new FormData();
      // Append text fields
      for (const key in project) {
        formData.append(key, project[key]);
      }
      formData.append("mainFileName", project.mainFileName);
      formData.append(
        "sourceFilesAndSubDirectories",
        JSON.stringify({
          include: includeTags,
          exclude: excludeTags,
        })
      );
      formData.append("src_dir", JSON.stringify(srcDirectoryTags));
      formData.append("include_dir", JSON.stringify(includeDirectoryTags));

      formData.append(
        "latestConfig",
        JSON.stringify(configurations.latestConfig)
      );
      // // Append selected generic tools if org type is "I"
      // if (organizationType === "I") {
      //   formData.append("selectedGenericTools", JSON.stringify(selectedGenericTools));
      // }
      // Append each selected file; the field "files" will be used in the backend.
      selectedFiles.forEach((file) => {
        // file.webkitRelativePath may contain the folder structure
        formData.append("files", file, file.webkitRelativePath || file.name);
      });

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
            body: formData,
          }
        );
        if (response.ok) {
          const data = await response.json();
          const project_id = data.project_id;
          localStorage.setItem("project_id", project_id);
          navigate(`/projects/activation?project_id=${project_id}`);
        } else {
          setError("Error creating project.");
        }
      } catch (error) {
        console.error("Error creating project:", error);
        setError("Error creating project: " + error.message);
      }
    } else {
      // For non-manual providers, use JSON
      const requestData = {
        ...project,
        mainFileName: project.mainFileName,
        sourceFilesAndSubDirectories: {
          include: includeTags,
          exclude: excludeTags,
        },
        src_dir: srcDirectoryTags,
        include_dir: includeDirectoryTags,
        latestConfig: configurations.latestConfig,
      };
      // // Include selected generic tools if org type is "I"
      // if (organizationType === "I") {
      //   requestData.selectedGenericTools = selectedGenericTools;
      // }
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/platform/projects`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(requestData),
          }
        );
        if (response.ok) {
          const data = await response.json();
          const project_id = data.project_id;
          localStorage.setItem("project_id", project_id);
          // Navigate to activation (Step 2)
          navigate(`/projects/activation?project_id=${project_id}`);
        } else {
          setError("Error creating project.");
        }
      } catch (error) {
        console.error("Error creating project:", error);
        setError("Error creating project: " + error.message);
      }
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 2 }}>
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              mt: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h5" gutterBottom>
              Create New Project
            </Typography>
            <Button
              variant="contained"
              className="athrv-dash-primary-button"
              sx={{
                mb: { xs: 1, sm: 0 },
                mt: { xs: 1, sm: 0 },
                alignSelf: { xs: "flex-start", sm: "auto" },
              }}
              onClick={() => navigate(`/projects`)}
            >
              Back to Project List
            </Button>
          </Box>
          {error && <Typography color="error">{error}</Typography>}
          <Typography variant="h6" gutterBottom>
            Step - 1
          </Typography>
          <Typography variant="h6" gutterBottom>
            Project Configuration
          </Typography>
          <form onSubmit={handleSubmit}>
            <TextField
              label="Project Name"
              name="name"
              value={project.name}
              onChange={handleChange}
              required
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              name="description"
              value={project.description}
              onChange={handleChange}
              required
              multiline
              rows={4}
              fullWidth
              sx={{ mb: 2 }}
            />

            {/* Repo Provider Dropdown */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="repo-provider-label">Repo Provider</InputLabel>
              <Select
                labelId="repo-provider-label"
                label="Repo Provider"
                name="repoProvider"
                value={project.repoProvider}
                onChange={handleRepoProviderChange}
              >
                <MenuItem value="github">GitHub</MenuItem>
                <MenuItem value="bitbucket" disabled>
                  Bitbucket (Coming Soon)
                </MenuItem>
                <MenuItem value="gitlab" disabled>
                  GitLab (Coming Soon)
                </MenuItem>
                <MenuItem value="manual" disabled>
                  Manual Upload (Coming Soon)
                </MenuItem>
              </Select>
            </FormControl>

            {/* Conditionally show clientRepoUrl field or file selector */}
            {project.repoProvider !== "manual" ? (
              <TextField
                label="Repo Url"
                name="clientRepoUrl"
                value={project.clientRepoUrl}
                onChange={handleChange}
                required
                multiline
                rows={2}
                fullWidth
                sx={{ mb: 2 }}
              />
            ) : (
              <Box sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  className="athrv-dash-primary-button"
                  component="label"
                >
                  Select Files & Folder
                  <input
                    type="file"
                    ref={fileInputRef}
                    hidden
                    multiple
                    onChange={handleFileSelection}
                  />
                </Button>
              </Box>
            )}

            <FormControl fullWidth required sx={{ mb: 2 }}>
              <InputLabel id="code-language-label">Code Language</InputLabel>
              <Select
                labelId="code-language-label"
                label="Code Language"
                name="codeLanguage"
                value={project.codeLanguage}
                onChange={handleChange}
              >
                <MenuItem value="c">C</MenuItem>
                <MenuItem value="cpp">C++</MenuItem>
                <MenuItem value="c,cpp">C and C++</MenuItem>
                <MenuItem value="kotlin">KOTLIN</MenuItem>
              </Select>
            </FormControl>

            {project.codeLanguage === "kotlin" && (
              <FormControl fullWidth required sx={{ mb: 2 }}>
                <InputLabel id="compiler-version-label">
                  Compiler Version
                </InputLabel>
                <Select
                  labelId="compiler-version-label"
                  label="Compiler Version"
                  name="compilerVersion"
                  value={project.compilerVersion}
                  onChange={handleChange}
                >
                  {javaVersions.map((version) => (
                    <MenuItem key={version} value={version}>
                      {version}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={useSubDirectory}
                  onChange={() => setUseSubDirectory(!useSubDirectory)}
                />
              }
              label="Custom Sources"
            />

            {useSubDirectory && (
              <SubDirectoryTagInput
                includeTags={includeTags}
                setIncludeTags={setIncludeTags}
                excludeTags={excludeTags}
                setExcludeTags={setExcludeTags}
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={useSrcDirectory}
                  onChange={() => setUseSrcDirectory(!useSrcDirectory)}
                />
              }
              label="Custom Sources Directrory"
            />
            {useSrcDirectory && (
              <SrcDirectoryTagInput
                srcDirectoryTags={srcDirectoryTags}
                setSrcDirectoryTags={setSrcDirectoryTags}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={useIncludeDirectory}
                  onChange={() => setUseIncludeDirectory(!useIncludeDirectory)}
                />
              }
              label="Custom Include Directory"
            />
            {useIncludeDirectory && (
              <IncludeDirectoryTagInput
                includeDirectoryTags={includeDirectoryTags}
                setIncludeDirectoryTags={setIncludeDirectoryTags}
              />
            )}
            {/* <Autocomplete
              freeSolo
              options={msuOptions}
              value={project.msu_definition || ""}
              onChange={(event, newValue) => handleMsuChange(event, newValue)}
              onInputChange={(event, newInputValue) =>
                handleMsuChange(event, newInputValue)
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="MSU Definition"
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
              ListboxProps={{
                style: {
                  maxHeight: 150,
                },
              }}
            /> */}
            <TextField
              label="Main File Full Path"
              name="mainFileName"
              value={project.mainFileName}
              onChange={handleChange}
              required
              fullWidth
              sx={{ mb: 2 }}
            />

            <TextField
              label="Created Date"
              name="created_date"
              type="date"
              value={project.created_date}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2, display: "none" }}
              disabled
            />
            <Typography variant="h6" gutterBottom>
              Step - 2
            </Typography>
            <Typography variant="h6" gutterBottom>
              {organizationType === "I"
                ? "Stages Configuration"
                : "Choose Stages"}
            </Typography>
            {tools.buildTool.length > 0 && (
              <FormControl fullWidth>
                <InputLabel id="build-tool-select-label">Build Tool</InputLabel>
                <Select
                  labelId="build-tool-select-label"
                  id="build-tool-select"
                  value={configurations.latestConfig.buildTool?.tool_id || ""}
                  label="Build Tool"
                  onChange={(e) => handleConfigChange(e, "buildTool")}
                  sx={{ mb: 2 }}
                >
                  <MenuItem value="">None</MenuItem>
                  {tools.buildTool.map((tool) => (
                    <MenuItem
                      key={tool.tool_id}
                      value={tool.tool_id}
                      name={tool.toolName}
                    >
                      {tool.toolName} (v{tool.toolVersion})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Multi-select for Static Analysis Tools with checkboxes */}
            {tools.staticAnalysisTools.length > 0 && (
              <FormControl fullWidth sx={{ mb: 1 }}>
                <InputLabel id="static-analysis-label">
                  Static Analysis Tools
                </InputLabel>
                <Select
                  labelId="static-analysis-label"
                  id="static-analysis-tools-select"
                  multiple
                  value={
                    configurations.latestConfig.staticAnalysisTools.map(
                      (tool) => tool.tool_id
                    ) || []
                  }
                  onChange={(e) => handleConfigChange(e, "staticAnalysisTools")}
                  input={<OutlinedInput label="Static Analysis Tools" />}
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const tool = tools.staticAnalysisTools.find(
                          (tool) => tool.tool_id === value
                        );
                        return tool ? tool.toolName : "";
                      })
                      .join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  {tools.staticAnalysisTools.map((tool) => (
                    <MenuItem
                      key={tool.tool_id}
                      value={tool.tool_id}
                      disabled={tool.immutable}
                    >
                      <Checkbox
                        checked={configurations.latestConfig.staticAnalysisTools.some(
                          (selectedTool) =>
                            selectedTool.tool_id === tool.tool_id
                        )}
                      />
                      <ListItemText
                        primary={tool.toolName}
                        secondary={`v${tool.toolVersion}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Unit Testing Tools Multi-select */}
            {tools.unitTestingTools.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="unit-testing-label">
                  Unit Testing Tools
                </InputLabel>
                <Select
                  labelId="unit-testing-label"
                  id="unit-testing-tools-select"
                  multiple
                  value={
                    configurations.latestConfig.unitTestingTools.map(
                      (tool) => tool.tool_id
                    ) || []
                  }
                  onChange={(e) => handleConfigChange(e, "unitTestingTools")}
                  input={<OutlinedInput label="Unit Testing Tools" />}
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const tool = tools.unitTestingTools.find(
                          (tool) => tool.tool_id === value
                        );
                        return tool ? tool.toolName : "";
                      })
                      .join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  {tools.unitTestingTools.map((tool) => (
                    <MenuItem key={tool.tool_id} value={tool.tool_id}>
                      <Checkbox
                        checked={configurations.latestConfig.unitTestingTools.some(
                          (selectedTool) =>
                            selectedTool.tool_id === tool.tool_id
                        )}
                      />
                      <ListItemText
                        primary={tool.toolName}
                        secondary={`v${tool.toolVersion}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Memory Testing Tools Multi-select */}
            {tools.memoryTestingTools.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="memory-testing-label">
                  Memory Testing Tools
                </InputLabel>
                <Select
                  labelId="memory-testing-label"
                  id="memory-testing-tools-select"
                  multiple
                  value={
                    configurations.latestConfig.memoryTestingTools.map(
                      (tool) => tool.tool_id
                    ) || []
                  }
                  onChange={(e) => handleConfigChange(e, "memoryTestingTools")}
                  input={<OutlinedInput label="Memory Testing Tools" />}
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const tool = tools.memoryTestingTools.find(
                          (tool) => tool.tool_id === value
                        );
                        return tool ? tool.toolName : "";
                      })
                      .join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  {tools.memoryTestingTools.map((tool) => (
                    <MenuItem key={tool.tool_id} value={tool.tool_id}>
                      <Checkbox
                        checked={configurations.latestConfig.memoryTestingTools.some(
                          (selectedTool) =>
                            selectedTool.tool_id === tool.tool_id
                        )}
                      />
                      <ListItemText
                        primary={tool.toolName}
                        secondary={`v${tool.toolVersion}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Cyber Testing Tools Multi-select */}
            {tools.cybersecurityAnalysisTools.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="cyber-testing-label">
                  Cyber Security Analysis Tools
                </InputLabel>
                <Select
                  labelId="cyber-testing-label"
                  id="cyber-testing-tools-select"
                  multiple
                  value={
                    configurations.latestConfig.cybersecurityAnalysisTools.map(
                      (tool) => tool.tool_id
                    ) || []
                  }
                  onChange={(e) =>
                    handleConfigChange(e, "cybersecurityAnalysisTools")
                  }
                  input={
                    <OutlinedInput label="Cyber Security Analysis Tools" />
                  }
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const tool = tools.cybersecurityAnalysisTools.find(
                          (tool) => tool.tool_id === value
                        );
                        return tool ? tool.toolName : "";
                      })
                      .join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  {tools.cybersecurityAnalysisTools.map((tool) => (
                    <MenuItem key={tool.tool_id} value={tool.tool_id}>
                      <Checkbox
                        checked={configurations.latestConfig.cybersecurityAnalysisTools.some(
                          (selectedTool) =>
                            selectedTool.tool_id === tool.tool_id
                        )}
                      />
                      <ListItemText
                        primary={tool.toolName}
                        secondary={`v${tool.toolVersion}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* FOSS Compliance Tools Multi-select */}
            {tools.fossComplianceTools.length > 0 && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="foss-compliance-label">
                  FOSS Compliance Tools
                </InputLabel>
                <Select
                  labelId="foss-compliance-label"
                  id="foss-compliance-tools-select"
                  multiple
                  value={
                    configurations.latestConfig.fossComplianceTools.map(
                      (tool) => tool.tool_id
                    ) || []
                  }
                  onChange={(e) =>
                    handleConfigChange(e, "fossComplianceTools")
                  }
                  input={
                    <OutlinedInput label="FOSS Compliance Analysis Tools" />
                  }
                  renderValue={(selected) =>
                    selected
                      .map((value) => {
                        const tool = tools.fossComplianceTools.find(
                          (tool) => tool.tool_id === value
                        );
                        return tool ? tool.toolName : "";
                      })
                      .join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  {tools.fossComplianceTools.map((tool) => (
                    <MenuItem key={tool.tool_id} value={tool.tool_id}>
                      <Checkbox
                        checked={configurations.latestConfig.fossComplianceTools.some(
                          (selectedTool) =>
                            selectedTool.tool_id === tool.tool_id
                        )}
                      />
                      <ListItemText
                        primary={tool.toolName}
                        secondary={`v${tool.toolVersion}`}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              type="submit"
              variant="contained"
              className="athrv-dash-primary-button"
              sx={{ mt: 2 }}
            >
              Create Project
            </Button>
          </form>
        </Paper>
        <br />
        <br />
      </Box>
    </Layout>
  );
};

export default CreateProject;
